import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { STLViewer } from "@/components/STLViewer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShoppingCart, AlertTriangle, Save } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import axiosInstance from "@/services/axios";
import { API } from "@/config/api";

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = "http://127.0.0.1:8000";
const API_URL = `${BACKEND_URL}/api/v1`;

export const ViewerPage = () => {
  const { fileId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditMode = searchParams.get("edit") === "true";
  const { isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [fileData, setFileData] = useState(null);
  const [metals, setMetals] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedMetal, setSelectedMetal] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [quantity, setQuantity] = useState("1");
  const [pricing, setPricing] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailKey, setThumbnailKey] = useState(0);
  const [metalPrices, setMetalPrices] = useState({});
  const [editingItemId, setEditingItemId] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // First, load all data
  useEffect(() => {
    loadData();
  }, [fileId]);

  // Then, handle edit mode data after metals are loaded
  useEffect(() => {
    if (isEditMode && metals.length > 0 && !initialLoadComplete) {
      const editingItem = sessionStorage.getItem("editingCartItem");
      if (editingItem) {
        const itemData = JSON.parse(editingItem);
        setEditingItemId(itemData.id);
        setSelectedMetal(itemData.metal_id);
        setSelectedServices(itemData.services || []);
        setQuantity(itemData.quantity.toString());
        sessionStorage.removeItem("editingCartItem");
        setThumbnailKey((prev) => prev + 1);
      }
      setInitialLoadComplete(true);
    } else if (!isEditMode && metals.length > 0 && !selectedMetal) {
      setSelectedMetal(metals[3]?.id);
      setInitialLoadComplete(true);
    }
  }, [isEditMode, metals, selectedMetal, initialLoadComplete]);

  useEffect(() => {
    if (selectedMetal && initialLoadComplete) {
      calculatePrice();
    }
  }, [selectedMetal, selectedServices, quantity, initialLoadComplete]);

  useEffect(() => {
    if (metals.length > 0 && initialLoadComplete) {
      calculateAllMetalPrices();
    }
  }, [selectedServices, metals, initialLoadComplete]);

  const loadData = async () => {
    try {
      const [fileRes, metalsRes, servicesRes] = await Promise.all([
        axiosInstance.get(`${API_URL}/files/${fileId}`),
        axiosInstance.get(`${API_URL}/metals`),
        axiosInstance.get(`${API_URL}/services`),
      ]);

      setFileData(fileRes.data);
      setMetals(metalsRes.data);
      setServices(servicesRes.data);

      setLoading(false);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load file data");
      setLoading(false);
    }
  };

  const calculateAllMetalPrices = async () => {
    if (!fileId || metals.length === 0) return;

    try {
      const results = await Promise.all(
        metals.map((metal) =>
          axiosInstance
            .post(`${API_URL}/price/calculate`, {
              file_id: fileId,
              metal_id: metal.id,
              services: selectedServices,
              quantity: 1,
            })
            .then((res) => ({
              metalId: metal.id,
              pricing: res.data,
            })),
        ),
      );

      const priceMap = {};
      results.forEach((r) => {
        priceMap[r.metalId] = r.pricing;
      });

      setMetalPrices(priceMap);

      if (selectedMetal && priceMap[selectedMetal]) {
        setPricing(priceMap[selectedMetal]);
      }
    } catch (err) {
      console.error("Failed to calculate metal prices", err);
    }
  };

  const calculatePrice = async () => {
    if (!selectedMetal) return;

    setCalculatingPrice(true);
    try {
      const response = await axiosInstance.post(`${API_URL}/price/calculate`, {
        file_id: fileId,
        metal_id: selectedMetal,
        services: selectedServices,
        quantity: parseInt(quantity),
      });
      setPricing(response.data);
    } catch (error) {
      console.error("Error calculating price:", error);
    } finally {
      setCalculatingPrice(false);
    }
  };

  const handleThumbnailReady = useCallback((thumbnailDataUrl) => {
    setThumbnail(thumbnailDataUrl);
  }, []);

  const syncCartWithServer = async (updatedCart) => {
    if (!isAuthenticated) return true;

    setIsSyncing(true);
    try {
      console.log("Syncing cart to server:", updatedCart);
      const response = await axiosInstance.post(API.auth.cart.sync, {
        items: updatedCart,
      });
      console.log("Server sync response:", response.data);
      return true;
    } catch (error) {
      console.error(
        "Failed to sync cart:",
        error.response?.data || error.message,
      );
      toast.error("Failed to sync cart with server");
      return false;
    } finally {
      setIsSyncing(false);
    }
  };

  const handleAddToCart = async () => {
    if (!pricing) return;

    const metalData = metals.find((m) => m.id === selectedMetal);
    const cartItem = {
      id: `${fileId}-${Date.now()}`,
      file_id: fileId,
      filename: fileData.filename,
      metal_id: selectedMetal,
      metal_name: metalData.name,
      metal_color: metalData.color_hex,
      services: selectedServices,
      quantity: parseInt(quantity),
      unit_price: pricing.total / quantity,
      line_total: pricing.total,
      thumbnail: thumbnail,
    };

    console.log("Adding item to cart:", cartItem);
    console.log("Is authenticated:", isAuthenticated);

    const existingCart = JSON.parse(localStorage.getItem("cart") || "[]");
    console.log("Existing cart:", existingCart);

    const updatedCart = [...existingCart, cartItem];
    console.log("Updated cart:", updatedCart);

    // Save to localStorage
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    console.log("Saved to localStorage");

    // Sync with server if logged in
    if (isAuthenticated) {
      const success = await syncCartWithServer(updatedCart);
      if (success) {
        toast.success("Added to cart!");
      }
    } else {
      toast.success("Added to cart!");
    }

    // Trigger storage event for other components
    window.dispatchEvent(new Event("storage"));

    navigate("/cart");
  };

  const handleUpdateInCart = async () => {
    if (!pricing || !editingItemId) return;

    const metalData = metals.find((m) => m.id === selectedMetal);
    const updatedItem = {
      id: editingItemId,
      file_id: fileId,
      filename: fileData.filename,
      metal_id: selectedMetal,
      metal_name: metalData.name,
      metal_color: metalData.color_hex,
      services: selectedServices,
      quantity: parseInt(quantity),
      unit_price: pricing.total / quantity,
      line_total: pricing.total,
      thumbnail: thumbnail,
    };

    const existingCart = JSON.parse(localStorage.getItem("cart") || "[]");
    const updatedCart = existingCart.map((item) =>
      item.id === editingItemId ? updatedItem : item,
    );

    localStorage.setItem("cart", JSON.stringify(updatedCart));

    // Sync with server if logged in
    if (isAuthenticated) {
      const success = await syncCartWithServer(updatedCart);
      if (success) {
        toast.success("Cart item updated successfully!");
      }
    } else {
      toast.success("Cart item updated successfully!");
    }

    // Trigger storage event for other components
    window.dispatchEvent(new Event("storage"));

    navigate("/cart");
  };

  const toggleService = (serviceId) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((s) => s !== serviceId)
        : [...prev, serviceId],
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const selectedMetalData = metals.find((m) => m.id === selectedMetal);

  return (
    <div className="min-h-screen bg-background py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {isEditMode && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-blue-800 font-medium">
              Editing item - Current metal:{" "}
              <span className="font-bold">{selectedMetalData?.name}</span>
            </p>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <div className="aspect-square mb-6">
              {selectedMetalData && (
                <STLViewer
                  key={thumbnailKey}
                  fileUrl={`${API_URL}/files/${fileId}/download`}
                  metalColor={selectedMetalData.color_hex}
                  onThumbnailReady={handleThumbnailReady}
                />
              )}
            </div>

            <div className="bg-white border border-border p-6 rounded-md">
              <h3 className="font-heading font-semibold text-xl mb-4">
                Model Information
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground font-medium">
                    Filename:
                  </span>
                  <span
                    className="font-mono font-semibold"
                    data-testid="model-filename"
                  >
                    {fileData.filename}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground font-medium">
                    Volume:
                  </span>
                  <span
                    className="font-mono font-semibold"
                    data-testid="model-volume"
                  >
                    {fileData.volume_cm3} cm³
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground font-medium">
                    Dimensions:
                  </span>
                  <span
                    className="font-mono font-semibold"
                    data-testid="model-dimensions"
                  >
                    {fileData.dimensions.x.toFixed(2)} ×{" "}
                    {fileData.dimensions.y.toFixed(2)} ×{" "}
                    {fileData.dimensions.z.toFixed(2)} cm
                  </span>
                </div>
                {pricing && (
                  <div className="flex justify-between py-2">
                    <span className="text-muted-foreground font-medium">
                      Model Weight:
                    </span>
                    <span
                      className="font-mono font-semibold text-lg"
                      data-testid="model-weight"
                    >
                      {pricing.weight_grams}g
                    </span>
                  </div>
                )}
              </div>

              {fileData.warnings && fileData.warnings.length > 0 && (
                <div
                  className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-sm"
                  data-testid="model-warnings"
                >
                  <div className="flex gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div className="text-sm">
                      {fileData.warnings.map((warning, idx) => (
                        <p key={idx} className="text-amber-800">
                          {warning}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-3xl font-bold mb-6">
              {isEditMode ? "Edit Your Order" : "Configure Your Order"}
            </h2>
            {pricing && (
              <div
                className="bg-secondary p-6 rounded-md mb-6"
                data-testid="pricing-breakdown"
              >
                <h3 className="font-heading font-semibold text-xl mb-4">
                  Price Breakdown
                </h3>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span>Base Manufacturing Fee:</span>
                    <span className="font-mono">
                      ${pricing.base_fee.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Metal Cost ({pricing.metal_name}):</span>
                    <span className="font-mono">
                      ${pricing.metal_cost.toFixed(2)}
                    </span>
                  </div>
                  {pricing.services_cost > 0 && (
                    <div className="flex justify-between">
                      <span>Additional Services:</span>
                      <span className="font-mono">
                        ${pricing.services_cost.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Quantity:</span>
                    <span className="font-mono">×{quantity}</span>
                  </div>
                </div>
                <div className="pt-4 border-t border-border flex justify-between items-center">
                  <span className="font-heading font-semibold text-2xl">
                    Total:
                  </span>
                  <span
                    className="font-heading font-bold text-3xl text-accent"
                    data-testid="total-price"
                  >
                    ${pricing.total.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <div className="flex items-center border border-input rounded-sm overflow-hidden w-40 mb-8">
              <button
                type="button"
                onClick={() =>
                  setQuantity((prev) =>
                    String(Math.max(1, parseInt(prev || "1") - 1)),
                  )
                }
                className="px-4 py-3 bg-gray-100 hover:bg-gray-200"
                disabled={isSyncing}
              >
                −
              </button>

              <input
                type="text"
                value={quantity}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    setQuantity(value);
                  }
                }}
                className="w-full text-center outline-none"
                disabled={isSyncing}
              />

              <button
                type="button"
                onClick={() =>
                  setQuantity((prev) => String(parseInt(prev || "0") + 1))
                }
                className="px-4 py-3 bg-gray-100 hover:bg-gray-200"
                disabled={isSyncing}
              >
                +
              </button>
            </div>

            <div className="mb-8">
              <h3 className="font-semibold text-lg mb-4">Select Metal</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {metals.map((metal) => (
                  <button
                    key={metal.id}
                    onClick={() => {
                      setSelectedMetal(metal.id);
                      setThumbnailKey((prev) => prev + 1);
                      if (metalPrices[metal.id]) {
                        setPricing(metalPrices[metal.id]);
                      }
                    }}
                    className={`p-4 border rounded-md transition-all duration-200 hover:border-accent ${
                      selectedMetal === metal.id
                        ? "border-accent bg-accent-light"
                        : "border-border"
                    }`}
                    data-testid={`metal-option-${metal.id}`}
                    disabled={isSyncing}
                  >
                    <div
                      className="w-full h-8 rounded-sm mb-2"
                      style={{ backgroundColor: metal.color_hex }}
                    />
                    <div className="text-xs font-medium text-center">
                      {metal.name} <br />
                      {metalPrices[metal.id]
                        ? `$${metalPrices[metal.id].total.toFixed(2)}`
                        : "—"}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="font-semibold text-lg mb-4">Optional Services</h3>
              <div className="space-y-3">
                {services.map((service) => (
                  <div
                    key={service.id}
                    className="flex items-start gap-3 p-3 border border-border rounded-sm hover:border-accent/30 transition-colors"
                  >
                    <Checkbox
                      id={service.id}
                      checked={selectedServices.includes(service.id)}
                      onCheckedChange={() => toggleService(service.id)}
                      data-testid={`service-checkbox-${service.id}`}
                      disabled={isSyncing}
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor={service.id}
                        className="text-sm font-medium cursor-pointer"
                      >
                        {service.name}
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        {service.description}
                      </p>
                    </div>
                    <div className="text-sm font-mono">${service.price}</div>
                  </div>
                ))}
              </div>
            </div>

            {isEditMode ? (
              <Button
                onClick={handleUpdateInCart}
                disabled={!pricing || calculatingPrice || isSyncing}
                className="w-full bg-accent hover:bg-accent-hover text-white px-8 py-6 text-sm uppercase tracking-widest"
                data-testid="update-cart-button"
              >
                {isSyncing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    SYNCING...
                  </>
                ) : calculatingPrice ? (
                  "Calculating..."
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Update Cart
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleAddToCart}
                disabled={!pricing || calculatingPrice || isSyncing}
                className="w-full bg-accent hover:bg-accent-hover text-white px-8 py-6 text-sm uppercase tracking-widest"
                data-testid="add-to-cart-button"
              >
                {isSyncing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    SYNCING...
                  </>
                ) : calculatingPrice ? (
                  "Calculating..."
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewerPage;
